import React, { useMemo, useState } from "react";
import { Search, Wrench, X } from "lucide-react";

/**
 * La casella per trovare un mezzo o un'attrezzatura fra quelli dell'azienda.
 *
 * Una tendina con dentro tutto il parco vuol dire scorrere centotrenta righe
 * per arrivare a una taglierina. Qui si scrive un pezzo di targa, di marca o
 * di modello e restano solo quelli che c'entrano.
 *
 * Sta in un file suo perché la usano il DDT e la segnalazione, e sono lo
 * stesso gesto: in entrambi i casi si sta indicando quale macchina.
 *
 * Va tenuta fuori dal componente che la usa: definita dentro, React la ricrea
 * a ogni lettera e il cursore salta via dalla casella.
 */
export default function CercaMezzo({
  mezzi, valore, onScegli, indice = 0, segnaposto, classe,
}) {
  const [q, setQ] = useState("");
  const [aperto, setAperto] = useState(false);
  const scelto = (mezzi || []).find((m) => m.id === valore);

  const nome = (m) => [m.identifier, [m.brand, m.model].filter(Boolean).join(" ")]
    .filter(Boolean).join(" · ");

  const trovati = useMemo(() => {
    const testo = q.trim().toLowerCase();
    if (!testo) return (mezzi || []).slice(0, 8);
    return (mezzi || []).filter((m) => [m.identifier, m.brand, m.model, m.category]
      .some((x) => String(x || "").toLowerCase().includes(testo))).slice(0, 8);
  }, [mezzi, q]);

  if (scelto) {
    return (
      <div className={`flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5 ${classe || ""}`}
           data-testid={`mezzo-scelto-${indice}`}>
        <Wrench className="w-3.5 h-3.5 text-[#0067b2] flex-shrink-0" />
        <span className="flex-1 min-w-0 truncate text-[#0067b2] font-medium">
          {nome(scelto)}
        </span>
        <button type="button" onClick={() => { onScegli(""); setQ(""); }}
                data-testid={`togli-mezzo-${indice}`}
                className="text-slate-400 hover:text-red-600 flex-shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${classe || ""}`}>
      <div className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-2">
        <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <input value={q} data-testid={`cerca-mezzo-${indice}`}
               onChange={(e) => { setQ(e.target.value); setAperto(true); }}
               onFocus={() => setAperto(true)}
               // Il ritardo serve perché il clic sulla riga arriva dopo il
               // blur: senza, l'elenco si chiude prima che la scelta passi.
               onBlur={() => setTimeout(() => setAperto(false), 150)}
               placeholder={segnaposto || "Cerca targa, marca o modello"}
               className="flex-1 min-w-0 h-9 text-xs bg-transparent outline-none text-slate-600" />
      </div>
      {aperto && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {trovati.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">
              Nessun mezzo per «{q}».
            </div>
          ) : trovati.map((m) => (
            <button key={m.id} type="button"
                    data-testid={`scegli-mezzo-${m.identifier}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onScegli(m.id); setQ(""); setAperto(false); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-baseline gap-2">
              <span className="font-semibold">{m.identifier}</span>
              <span className="flex-1 min-w-0 truncate text-slate-600">
                {[m.brand, m.model].filter(Boolean).join(" ")}
              </span>
              <span className="text-[10px] text-slate-400 flex-shrink-0">{m.category}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
